/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { Case, useCaseMutations, useCaseSectionsQuery, useCasesQuery, useCaseStatesQuery } from 'src/services/case';
import { TFunction } from 'i18next';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import { useNavigationItems } from 'src/hooks/useNavigationItems';
import styles from './styles/CasesOverview.module.scss';
import { DataView } from '../components/ui/TableOverview/DataView';
import NoDataPlaceholder from '../components/layouts/NoDataPlaceholder/NoDataPlaceholder';
import { useDataViewController } from '../hooks/useDataViewController';
import { SearchModels } from '../components/ui/TableOverview/utils/SearchFieldRegistry';
import { useQuickFilterQuery } from '../services/utility';
import { OverlayNavProvider } from '../providers/OverlayNavProvider';

export function getFormattedDay(dateIn: Date | string) {
  const date = typeof dateIn === 'string' ? new Date(dateIn) : dateIn;
  return new Intl.DateTimeFormat('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

export function getFormattedTime(dateIn: Date | string) {
  const date = typeof dateIn === 'string' ? new Date(dateIn) : dateIn;
  return new Intl.DateTimeFormat('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false, // Use 24-hour format
  }).format(date);
}

export function getCaseLabel(statusName: string, t: TFunction): string {
  return t(`status.${statusName?.toLowerCase()}`, { defaultValue: statusName });
}

export function CaseStatusBadge({ status }: { status: Case['state'] }) {
  const { t } = useTranslation();
  const badgeType: Record<string, BadgeType> = {
    draft: BadgeType.Neutral,
    done: BadgeType.Successful,
    backlog: BadgeType.Neutral4,
    locked: BadgeType.Error,
    progress: BadgeType.Warning,
  };

  return (
    <Badge
      className="max-w-min"
      label={getCaseLabel(status.name, t)}
      expanded
      type={badgeType[status.name?.toLowerCase()] ?? BadgeType.Neutral}
      colorOverride={status.badge_color}
    />
  );
}

export function getModelName(model: string | undefined, modelNames: Record<string, string> | null): string | null {
  if (!model) return null;
  const label = modelNames?.[model] ?? model;
  return label;
}

export function getPriorityLabel(priority: string, t: TFunction): { label: string; badgeType: BadgeType } {
  const nameMapping = {
    '0': 'None',
    '1': 'Low',
    '2': 'Middle',
    '3': 'High',
  };

  const badgeTypeMapping = {
    '0': BadgeType.Neutral,
    '1': BadgeType.Successful,
    '2': BadgeType.Warning,
    '3': BadgeType.Error,
  };

  const badgeType = badgeTypeMapping[priority as keyof typeof badgeTypeMapping] ?? BadgeType.Neutral;

  if (!(priority in nameMapping)) {
    return { label: priority, badgeType };
  }

  const prioString = nameMapping[priority as keyof typeof nameMapping]!;
  const prioLowerCase = prioString?.toLowerCase();

  return { label: t(`case.priority_${prioLowerCase}`, { defaultValue: prioString }), badgeType };
}

export function CasePriorityBadge({ status }: { status: Case['priority'] }) {
  const { t } = useTranslation();
  if (status === '0') return null;

  const { label, badgeType } = getPriorityLabel(status, t);
  return <Badge className="max-w-min" label={label} showDot expanded showBg={false} type={badgeType} />;
}

function CasesOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: quickFilters } = useQuickFilterQuery('project.task');
  const { data: caseStates } = useCaseStatesQuery();
  const { setDoneMutation, setCancelledMutation } = useCaseMutations();
  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('cases');

  const labels = {
    timestamp: t('case.timestamp'),
    section: t('case.section'),
    priority: t('case.priority'),
    topic: t('case.topic'),
    state: t('case.state'),
    assignedUser: t('case.assignedUser'),
    createdFrom: t('case.createdFrom'),
  } as const;

  const sections = useCaseSectionsQuery()?.data as { key: string; label: string }[] | undefined;
  const modelNames = sections ? Object.fromEntries(sections.map(model => [model.key, model.label])) : null;

  const columnHelper = createColumnHelper<Case>();
  const columns: ColumnDef<Case, any>[] = [
    columnHelper.accessor(row => row.id, {
      id: 'id',
      size: 80,
      enableHiding: false,
      meta: { label: t('general.id') },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex flex-col gap-y-4">
          <span className="typo-text-14--bold text-full"> {info.getValue()} </span>
        </div>
      ),
    }),

    columnHelper.accessor(row => row.timestamp, {
      id: 'create_date',
      size: 80,
      meta: {
        label: labels.timestamp,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const d = info.getValue() as Date | undefined;
        if (!d) return null;
        const day = getFormattedDay(d);
        const time = getFormattedTime(d);
        return (
          <div className="flex flex-col gap-y-4">
            <div className="typo-text-14">{day}</div>
            <div className="typo-text-12 text-faded">{time}</div>
          </div>
        );
      },
    }),

    columnHelper.accessor(row => row.topic, {
      id: 'name',
      size: 240,
      meta: {
        label: labels.topic,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <div className={`typo-text-14--bold ${styles.title}`}>{info.getValue()?.toString()}</div>,
    }),

    columnHelper.accessor(row => row.section, {
      id: 'resource_model',
      size: 80,
      meta: {
        label: labels.section,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const modelName = getModelName(info.getValue() as string | undefined, modelNames);
        return <div className="typo-text-14 text-faded">{modelName || ''}</div>;
      },
    }),

    columnHelper.accessor(row => row.assigned_user?.[0]?.name, {
      id: 'user_ids',
      size: 80,
      meta: {
        label: labels.assignedUser,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        return <div className="typo-text-14 text-faded whitespace-nowrap">{info.getValue()?.toString()}</div>;
      },
    }),

    columnHelper.accessor(row => row.created_from?.name, {
      id: 'creator_name',
      size: 80,
      meta: {
        label: labels.createdFrom,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <div className="typo-text-14 text-faded whitespace-nowrap">{info.getValue()?.toString()}</div>,
    }),

    columnHelper.accessor(row => row.priority, {
      id: 'priority',
      size: 80,
      meta: {
        label: labels.priority,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: ({ row }) => {
        const data = row.original;
        return <CasePriorityBadge status={data.priority} />;
      },
    }),

    columnHelper.accessor(row => row.state, {
      id: 'stage_id',
      size: 80,
      meta: {
        label: labels.state,
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => {
        const state = info.getValue() as Case['state'] | undefined;
        return state ? <CaseStatusBadge status={state} /> : null;
      },
    }),
  ];

  const tabs =
    caseStates && (caseStates as any[]).length > 0
      ? [
          { label: t('integrations.all'), key: 'all' },
          ...(caseStates as any[]).map((state: any) => ({
            label: getCaseLabel(state.display_name, t),
            key: state.display_name,
          })),
        ]
      : null;

  const controller = useDataViewController();
  const [entityIds, setEntityIds] = useState<(string | number)[]>([]);

  return tabs ? (
    <OverlayNavProvider entityIds={entityIds} basePath="/cases">
      <DataView<Case>
        onDataLoaded={items => setEntityIds(items.map(i => i.id).filter((id): id is number => id != null))}
        filterModelId="project.task"
        withSaveFilters
        controller={controller}
        modelKey={SearchModels.Case}
        headline={t('navbar.cases')}
        emptyState={<NoDataPlaceholder headline={t('general.empty_cases')} />}
        allowBulkActions={accessRights?.read_write}
        displayActionsColumn={accessRights?.read_write}
        actions={[
          {
            title: t('case.create_case'),
            icon: 'plus',
            onExecute: () => {
              navigate('/cases/create');
            },
            showInToolbar: accessRights?.read_write,
          },
          {
            title: t('general.view_details'),
            icon: 'edit',
            isRowClickAction: true,
            onExecute: selected => {
              if (selected.length > 0) {
                navigate(`/cases/${selected[0].id}`);
              }
            },
            showInContextMenu: true,
          },
          {
            title: t('case.set_done'),
            titlePlural: t('case.set_done_plural'),
            icon: 'checkmark',
            showInContextMenu: true,
            bulkSupport: true,
            onExecute: async selected => {
              const updatePromises = selected.map(async s => {
                if (s.id !== null)
                  return setDoneMutation.mutateAsync({
                    params: {
                      path: {
                        model: 'project.task',
                      },
                    },
                    body: [s.id],
                  });
                return null;
              });
              await Promise.all(updatePromises);
            },
          },
          {
            title: t('case.set_cancelled'),
            titlePlural: t('case.set_cancelled_plural'),
            icon: 'close',
            showInContextMenu: true,
            bulkSupport: true,
            onExecute: async selected => {
              const updatePromises = selected.map(async s => {
                if (s.id !== null)
                  return setCancelledMutation.mutateAsync({
                    params: {
                      path: {
                        model: 'project.task',
                      },
                    },
                    body: [s.id],
                  });
                return null;
              });
              await Promise.all(updatePromises);
            },
          },
        ]}
        kpiModel="project.task"
        columns={columns}
        useDataQuery={useCasesQuery}
        tabs={[
          { label: t('general.all'), key: 'all', value: quickFilters?.map(q => q.value).reduce((a, b) => a + b, 0) },
          ...(quickFilters ?? []),
        ]}
        getDomainForTab={(tabKey, index) => (index === 0 ? '[]' : `[("stage_id", "=", ${tabKey})]`)}
      />
    </OverlayNavProvider>
  ) : undefined;
}

export default CasesOverview;
